/**
 * The decisions the Expense Claim Request screen makes, kept out of the
 * component so they can be tested without rendering it.
 *
 * Each of these used to live inline in ClaimFormPage. They are the parts a
 * requester actually feels: which months the Date Covered list offers, which
 * receipts survive a change of month, and what stops the Submit button.
 */
import {
  claimablePeriods,
  detectRowAnomalies,
  validateReceiptDate,
  type RowAnomaly,
} from '@gdec/shared';
import type { DraftRow } from './draft';

export interface PeriodOption {
  value: string;
  /** Listed so the control is not blank, but not selectable. */
  closed: boolean;
}

/**
 * What the Date Covered selector offers: the open periods, plus whatever this
 * claim is already on if that has since closed.
 *
 * A claim disapproved in August and resubmitted in October covers a period
 * nobody may newly claim. Dropping it from the list would silently blank the
 * control and leave the form pointing at a period it no longer shows, so it
 * is listed and disabled — visible, unselectable, and the submit-time rule
 * still explains why.
 */
export function periodSelectorOptions(currentPeriod: string, now: Date = new Date()): PeriodOption[] {
  const open = claimablePeriods(now).map((value) => ({ value, closed: false }));
  if (currentPeriod && !open.some((p) => p.value === currentPeriod)) {
    return [...open, { value: currentPeriod, closed: true }];
  }
  return open;
}

/**
 * Re-check every row against a newly chosen period.
 *
 * A receipt dated outside the new month cannot silently ride along on the
 * claim, so it is dropped and counted for the message. A row with no date yet
 * is kept: there is nothing to check, and the anomaly panel will ask for one.
 */
export function rowsForPeriod(
  rows: readonly DraftRow[],
  period: string,
): { kept: DraftRow[]; dropped: number } {
  const kept: DraftRow[] = [];
  let dropped = 0;
  for (const row of rows) {
    if (!row.date) {
      kept.push(row);
      continue;
    }
    const check = validateReceiptDate({ date: row.date, date_ambiguous: false }, period);
    if (check.status === 'ok') kept.push(row);
    else dropped += 1;
  }
  return { kept, dropped };
}

export interface RowFindings {
  row: DraftRow;
  /** 1-based, as the requester sees the table. */
  line: number;
  items: RowAnomaly[];
}

/**
 * Every outstanding problem on every row. Any entry here blocks Submit.
 *
 * Deliberately a full list rather than the first failure: someone fixing one
 * row at a time, learning of the next problem only after correcting the last,
 * has no idea how far from done they are.
 *
 * `rates` is Finance's current table. The live rate wins over whatever was on
 * the row when it was read, so a missing rate that Finance has since added
 * clears on the next check instead of persisting because of a stale null.
 */
export function outstandingFindings(
  rows: readonly DraftRow[],
  period: string,
  rates: Readonly<Record<string, number>>,
): RowFindings[] {
  const out: RowFindings[] = [];
  for (const [index, row] of rows.entries()) {
    const fxRate = row.currency && row.currency !== 'PHP'
      ? (rates[row.currency] ?? row.fxRate ?? null)
      : null;
    const items = detectRowAnomalies({ ...row, fxRate }, period, rows);
    // A finding only the server could make — a receipt already used on
    // another claim, or a file it could not read.
    if (row.serverAnomaly) {
      items.push(row.serverAnomaly as RowAnomaly);
    }
    if (items.length > 0) out.push({ row, line: index + 1, items });
  }
  return out;
}
